import { ActorSubclass, HttpAgent } from '@icp-sdk/core/agent'
import { Principal } from '@icp-sdk/core/principal'
import { config } from '../appConfig'
import { UNKNOWN_ERROR } from '../constants/swapErrors'
import { approveToken, transferToken } from '../handlers/walletActions/wallet'
import {
  idlFactory as idlSwapFactory,
  PoolData,
  _SERVICE as SwapFactory,
} from '../idls/icpswap/swapFactory.did'
import { idlFactory as idlPoolFactory, _SERVICE as SwapPool } from '../idls/icpswap/swapPool.did'
import { ICToken } from '../interfaces/common'
import {
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
  SwapStep,
  SwapTokenPair,
} from '../interfaces/swapInterface'
import { createActor } from '../utils/createActor'
import { subaccountFromPrincipal } from '../utils/subaccount'

const SWAP_FACTORY_CANISTER_ID = '4mmnk-kiaaa-aaaag-qbllq-cai'

class ICPSwap {
  public identityAgent: HttpAgent
  public anonymousActor: ActorSubclass<SwapFactory>

  constructor(identityAgent: HttpAgent) {
    this.identityAgent = identityAgent
    this.anonymousActor = createActor(idlSwapFactory, {
      canisterId: SWAP_FACTORY_CANISTER_ID,
      agent: config.getAgent(),
    })
  }

  parsePool(pool: PoolData): SwapTokenPair {
    const { fee, canisterId, token0, token1 } = pool

    return {
      swapCanisterPrincipal: canisterId.toString(),
      feeRate: Number(fee),
      token0: {
        principal: token0.address,
        standard: token0.standard,
      },
      token1: {
        principal: token1.address,
        standard: token1.standard,
      },
      provider: SwapProvider.ICPSWAP,
    }
  }

  getPoolActor(poolCanisterPrincipal: string, authenticated: boolean) {
    return createActor<SwapPool>(idlPoolFactory, {
      canisterId: poolCanisterPrincipal,
      agent: authenticated ? this.identityAgent : config.getAgent(),
    })
  }

  async getQuote(
    amount: bigint,
    fromToken: ICToken,
    toToken: ICToken
  ): Promise<SwapProviderQuote | SwapProviderQuoteFetchError> {
    try {
      const pool = await this.#getPool(fromToken, toToken)
      const actor = this.getPoolActor(pool.canisterId.toString(), false)

      const response = await actor.quote({
        amountIn: amount.toString(),
        amountOutMinimum: '0',
        zeroForOne: fromToken.address === pool.token0.address,
      })

      if ('ok' in response) {
        return {
          price: Number(response.ok),
          priceHuman: Number(response.ok) * 10 ** -toToken.decimals,
          provider: SwapProvider.ICPSWAP,
        }
      } else {
        return { error: UNKNOWN_ERROR, provider: SwapProvider.ICPSWAP }
      }
    } catch (e) {
      return { error: UNKNOWN_ERROR, provider: SwapProvider.ICPSWAP }
    }
  }

  async swap(
    fromToken: ICToken,
    toToken: ICToken,
    amount: bigint,
    limitPrice: number,
    walletPrincipal: string,
    setStep: (step: SwapStep) => void
  ) {
    setStep(SwapStep.Transfer)

    const pool = await this.#getPool(fromToken, toToken)

    if (fromToken.standard === 'ICRC1') {
      await transferToken({
        receiver: {
          address: pool.canisterId.toString(),
          subaccount: Array.from(subaccountFromPrincipal(walletPrincipal)),
        },
        amount: amount.toString(),
        standard: fromToken.standard,
        tokenCanisterId: fromToken.address,
      })
    } else {
      setStep(SwapStep.Approve)

      await approveToken({
        standard: fromToken.standard,
        tokenCanisterId: fromToken.address,
        spender: {
          address: pool.canisterId.toString(),
        },
        amount: (amount + fromToken.fee).toString(),
      })
    }

    const actor = this.getPoolActor(pool.canisterId.toString(), true)

    const depositMethod = fromToken.standard === 'ICRC1' ? actor.deposit : actor.depositFrom

    setStep(SwapStep.Deposit)

    const depositResponse = await depositMethod({
      fee: fromToken.fee,
      token: fromToken.address,
      amount,
    })

    if ('err' in depositResponse) {
      return Promise.reject(Object.values(depositResponse.err).join(','))
    }

    if ('ok' in depositResponse) {
      const depositedAmount = depositResponse.ok

      setStep(SwapStep.Swap)
      const swapResponse = await actor.swap({
        amountIn: (depositedAmount - fromToken.fee).toString(),
        zeroForOne: fromToken.address === pool.token0.address,
        amountOutMinimum: limitPrice.toString(),
      })

      if ('err' in swapResponse) {
        if (fromToken.standard === 'ICRC1') {
          setStep(SwapStep.ErrorFallback)

          await actor.withdraw({
            fee: fromToken.fee,
            token: fromToken.address,
            amount: depositedAmount - fromToken.fee,
          })
        }
        return Promise.reject(Object.values(swapResponse.err).join(','))
      }

      if ('ok' in swapResponse) {
        const swappedAmount = swapResponse.ok

        setStep(SwapStep.Withdraw)

        const withdrawResponse = await actor.withdraw({
          fee: toToken.fee,
          token: toToken.address,
          amount: swappedAmount - toToken.fee,
        })

        if ('ok' in withdrawResponse) {
          return Number(withdrawResponse.ok)
        }

        if ('err' in withdrawResponse) {
          return Promise.reject(Object.values(withdrawResponse.err).join(','))
        }
      }
    }

    return Promise.reject('Unknown status')
  }

  async getPairs() {
    const pools = await this.anonymousActor.getPools()

    if ('ok' in pools) {
      return pools.ok.map(this.parsePool.bind(this))
    }

    if ('err' in pools) {
      return Promise.reject(pools.err)
    }

    return []
  }

  async getPoolUnusedBalance(pool: SwapTokenPair, userPrincipal: string) {
    const actor = this.getPoolActor(pool.swapCanisterPrincipal, false)
    const unusedBalance = await actor.getUserUnusedBalance(Principal.fromText(userPrincipal))

    if ('err' in unusedBalance) {
      return Promise.reject(unusedBalance.err)
    }

    if ('ok' in unusedBalance) {
      return {
        balance0: Number(unusedBalance.ok.balance0),
        balance1: Number(unusedBalance.ok.balance1),
      }
    }

    return Promise.reject('Unknown response')
  }

  async #getPool(fromToken: ICToken, toToken: ICToken) {
    const poolResponse = await this.anonymousActor.getPool({
      fee: 3000n,
      token0: { address: fromToken.address, standard: fromToken.standard },
      token1: { address: toToken.address, standard: toToken.standard },
    })

    if ('ok' in poolResponse) {
      return poolResponse.ok
    } else {
      throw poolResponse.err
    }
  }
}

export default ICPSwap
